/**
 * DemoModeIndicator Component
 * Phase D4.5.5: Visual Demo Guardrails
 * 
 * Shows a subtle indicator when Demo Mode is active.
 * Also shows hints when voice failures occur.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useDemoMode, MAX_CONSECUTIVE_FAILURES } from '@/providers/DemoModeProvider';

export function DemoModeIndicator() {
  const { isDemoMode, consecutiveFailures } = useDemoMode();

  if (!isDemoMode) return null;

  const showWarning = consecutiveFailures >= MAX_CONSECUTIVE_FAILURES - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-4 right-4 z-[100] flex flex-col items-end gap-2"
      >
        {/* Demo Mode Badge */}
        <div className="px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-200 text-xs font-medium tracking-wide uppercase">
              Demo Mode
            </span>
          </div>
        </div>

        {/* Voice Trouble Warning */}
        <AnimatePresence>
          {showWarning && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="px-3 py-2 rounded-lg bg-zaxia-dark/90 border border-white/10 backdrop-blur-sm max-w-xs"
            >
              <p className="text-white/70 text-xs">
                💡 <span className="text-white/90">Tip:</span> Try using the remote to navigate, 
                or say <span className="text-amber-300">"restart session"</span> to reset.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Demo-friendly Empty State
 * Use this instead of showing blank screens in demo mode
 */
interface DemoEmptyStateProps {
  title?: string;
  message?: string;
  suggestion?: string;
}

export function DemoEmptyState({
  title = "Nothing here yet",
  message = "This area is ready for your bookings.",
  suggestion = 'Try saying "book a spa treatment" or "show me restaurants"',
}: DemoEmptyStateProps) {
  const { isDemoMode } = useDemoMode();

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="w-16 h-16 mb-4 rounded-full bg-white/5 flex items-center justify-center">
        <span className="text-3xl">🏨</span>
      </div>
      <h3 className="text-white/90 text-lg font-medium mb-2">{title}</h3>
      <p className="text-white/50 text-sm mb-4">{message}</p>
      {isDemoMode && (
        <p className="text-amber-300/70 text-xs italic">
          💡 {suggestion}
        </p>
      )}
    </div>
  );
}

/**
 * Demo Recovery Prompt
 * Shows when things go wrong to help recover gracefully
 */
interface DemoRecoveryPromptProps {
  isVisible: boolean;
  onReset?: () => void;
  onDismiss?: () => void;
}

export function DemoRecoveryPrompt({ isVisible, onReset, onDismiss }: DemoRecoveryPromptProps) {
  const { isDemoMode } = useDemoMode();

  if (!isDemoMode || !isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100]"
    >
      <div className="px-6 py-4 rounded-2xl bg-zaxia-dark/95 border border-white/10 backdrop-blur-lg shadow-2xl">
        <p className="text-white/90 text-sm mb-3">
          Something didn't work as expected. Would you like to start fresh?
        </p>
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 text-sm hover:bg-amber-500/30 transition-colors"
          >
            Reset Demo
          </button>
          <button
            onClick={onDismiss}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:bg-white/10 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default DemoModeIndicator;
